// src/features/dashboard/components/EmployeeIdleTime/EmployeeIdleKPICards.tsx

import {
  Clock3,
  Percent,
  Hourglass,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';

import {
  EmployeeIdleKpiResponse,
} from '../../types/Employee/employeeIdleTime.types';

interface Props {
  data?: EmployeeIdleKpiResponse | null;
}


/* ============================================================
   TIME FORMATTER
   ============================================================ */

/**
 * Converts API HH:mm:ss values into:
 *
 * HH h MM m
 *
 * Examples:
 *
 * 01:12:36 -> 1 h 12 m
 * 07:12:00 -> 7 h 12 m
 * 00:45:30 -> 45 m
 * 08:00:00 -> 8 h 0 m
 *
 * Seconds are intentionally ignored for UI display.
 */
const formatTime = (
  time?: string | null,
): string => {

  if (!time) {
    return '--';
  }

  const value =
    String(time).trim();

  if (!value) {
    return '--';
  }

  const parts =
    value.split(':');


  /* ----------------------------------------------------------
     Expected HH:mm:ss
     ---------------------------------------------------------- */

  if (parts.length === 3) {

    const hours =
      Number(parts[0]);

    const minutes =
      Number(parts[1]);

    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes)
    ) {
      return '--';
    }

    if (hours > 0) {
      return `${hours} h ${minutes} m`;
    }

    return `${minutes} m`;
  }


  /* ----------------------------------------------------------
     Already HH:mm
     ---------------------------------------------------------- */

  if (parts.length === 2) {

    const hours =
      Number(parts[0]);

    const minutes =
      Number(parts[1]);

    if (
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes)
    ) {
      return '--';
    }

    if (hours > 0) {
      return `${hours} h ${minutes} m`;
    }

    return `${minutes} m`;
  }


  /* ----------------------------------------------------------
     Unknown format
     ---------------------------------------------------------- */

  return value;
};


/* ============================================================
   MAIN COMPONENT
   ============================================================ */

const EmployeeIdleKPICards = ({
  data,
}: Props) => {


  /* ==========================================================
     SAFE NUMERIC VALUES
     ========================================================== */

  const idleChange =
    Number(
      data?.idleTodayPercentageChange ?? 0,
    );

  const teamVariance =
    Number(
      data?.teamAverageVariance ?? 0,
    );

  const idlePercentage =
    Number(
      data?.idlePercentage ?? 0,
    );

  const largestSpikeMinutes =
    Number(
      data?.largestSpikeMinutes ?? 0,
    );


  /* ==========================================================
     KPI CARDS
     ========================================================== */

  const cards = [

    /* --------------------------------------------------------
       IDLE HOURS
       -------------------------------------------------------- */

    {
      title: 'Idle Hours',

      value:
        formatTime(
          data?.idleToday,
        ),

      subtitle:
        idleChange === 0
          ? 'same as last week'
          : `${idleChange > 0 ? '+' : ''}${idleChange.toFixed(1)}% vs last week`,

      icon: Clock3,

      bg: 'bg-[#FFF4E5]',

      color: 'text-[#F97316]',

      subtitleColor:
        'text-[#2563EB]',
    },


    /* --------------------------------------------------------
       IDLE %
       -------------------------------------------------------- */

    {
      title: 'Idle %',

      value:
        idlePercentage.toFixed(2),

      sub: '%',

      subtitle:
        `of ${formatTime(
          data?.loggedHours,
        )} logged · target ≤15%`,

      icon: Percent,

      bg: 'bg-[#ECFDF3]',

      color: 'text-[#10B981]',

      subtitleColor:
        'text-[#667085]',
    },


    /* --------------------------------------------------------
       WTD AVG IDLE
       -------------------------------------------------------- */

    {
      title:
        'WTD Avg Idle',

      value:
        formatTime(
          data?.wtdAverageIdle,
        ),

      subtitle:
        'Per day this week',

      icon: Hourglass,

      bg: 'bg-[#EEF4FF]',

      color: 'text-[#3B82F6]',

      subtitleColor:
        'text-[#667085]',
    },


    /* --------------------------------------------------------
       LARGEST SPIKE
       -------------------------------------------------------- */

    {
      title:
        'Largest Spike',

      value:
        Number.isFinite(
          largestSpikeMinutes,
        )
          ? largestSpikeMinutes.toString()
          : '--',

      sub: 'min',

      subtitle:
        'Largest idle spike today',

      icon:
        AlertTriangle,

      bg: 'bg-[#FEF3F2]',

      color:
        'text-[#EF4444]',

      subtitleColor:
        'text-[#667085]',
    },


    /* --------------------------------------------------------
       VS TEAM AVG
       -------------------------------------------------------- */

    {
      title:
        'vs Team Avg',

      value:
        `${teamVariance >= 0 ? '+' : ''}${teamVariance.toFixed(2)}`,

      sub: 'min',

      subtitle:
        teamVariance <= 0
          ? 'Below team avg · good'
          : 'Above team avg',

      icon:
        BarChart3,

      bg: 'bg-[#F3E8FF]',

      color:
        'text-[#7C3AED]',

      subtitleColor:
        teamVariance <= 0
          ? 'text-[#16A34A]'
          : 'text-[#DC2626]',
    },

  ];


  /* ==========================================================
     RENDER
     ========================================================== */

  return (

    <div
      className="
        grid
        w-full
        min-w-0

        grid-cols-1
        gap-3

        sm:grid-cols-2
        sm:gap-4

        lg:grid-cols-5
        lg:gap-4
      "
    >

      {cards.map((card) => {

        const Icon =
          card.icon;


        return (

          <div
            key={card.title}
            className="
              flex
              min-w-0
              w-full
              max-w-none
            "
          >

            {/* ==================================================
                KPI CARD
                ================================================== */}

            <div
              className="
                flex
                min-h-[82px]
                w-full
                min-w-0
                flex-1
                items-start
                overflow-hidden
                rounded-[18px]
                border
                border-[#D0D5DD]
                bg-white
                px-[14px]
                py-[12px]
                transition-all
                duration-200
                hover:shadow-md

                sm:min-h-[88px]
                sm:px-[14px]
                sm:py-[12px]

                lg:min-h-[82px]
                lg:px-[14px]
                lg:py-[12px]
            "
            >

              {/* =================================================
                  CARD CONTENT ROW
              ================================================= */}

              <div
                className="
                  flex
                  min-w-0
                  w-full
                  items-start
                  gap-3
                "
              >

                {/* =================================================
                    ICON
                ================================================= */}

                <div
                  className={`
                    flex
                    h-[30px]
                    w-[30px]
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    ${card.bg}
                  `}
                >

                  <Icon
                    size={16}
                    strokeWidth={2.2}
                    className={
                      card.color
                    }
                  />

                </div>


                {/* =================================================
                    CONTENT
                ================================================= */}

                <div
                  className="
                    min-w-0
                    flex-1
                    overflow-hidden
                  "
                >

                  {/* ---------------------------------------------
                      TITLE
                  --------------------------------------------- */}

                  <h4
                    className="
                      min-w-0
                      overflow-hidden
                      text-ellipsis
                      whitespace-nowrap
                      text-[11px]
                      font-semibold
                      leading-[16px]
                      text-[#101828]

                      sm:text-[12px]

                      lg:text-[11px]
                  "
                    title={
                      card.title
                    }
                  >
                    {card.title}
                  </h4>


                  {/* ---------------------------------------------
                      VALUE
                  --------------------------------------------- */}

                  <div
                    className="
                      mt-[2px]
                      flex
                      min-w-0
                      items-end
                      gap-[2px]
                    "
                  >

                    <span
                      className="
                        min-w-0
                        overflow-hidden
                        text-ellipsis
                        whitespace-nowrap
                        text-[17px]
                        font-bold
                        leading-[20px]
                        text-[#101828]

                        sm:text-[18px]

                        lg:text-[18px]
                      "
                      title={
                        `${card.value}${card.sub ?? ''}`
                      }
                    >
                      {card.value}
                    </span>


                    {card.sub && (

                      <span
                        className="
                          mb-[1px]
                          shrink-0
                          text-[11px]
                          text-[#667085]

                          sm:text-[12px]
                        "
                      >
                        {card.sub}
                      </span>

                    )}

                  </div>


                  {/* ---------------------------------------------
                      SUBTITLE
                  --------------------------------------------- */}

                  {card.subtitle && (

                    <p
                      className={`
                        mt-[3px]
                        min-w-0
                        overflow-hidden
                        text-ellipsis
                        whitespace-nowrap
                        text-[10px]
                        leading-[13px]
                        ${card.subtitleColor}

                        sm:text-[11px]
                      `}
                      title={
                        card.subtitle
                      }
                    >
                      {card.subtitle}
                    </p>

                  )}

                </div>

              </div>

            </div>

          </div>

        );

      })}

    </div>

  );
};


export default EmployeeIdleKPICards;